import { useCallback, useEffect, useRef, useState } from 'react'

import { t } from './i18n'
import { type Credential, decideLobby, type Knock, listLobby } from './lobby'

const POLL_MS = 3_000

/** Two short notes when someone new knocks. Generated, so there is no asset to load or cache. */
function chime() {
  try {
    const ctx = new AudioContext()
    const gain = ctx.createGain()
    gain.gain.setValueAtTime(0.06, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6)
    gain.connect(ctx.destination)
    for (const [i, freq] of [660, 880].entries()) {
      const osc = ctx.createOscillator()
      osc.frequency.value = freq
      osc.connect(gain)
      osc.start(ctx.currentTime + i * 0.15)
      osc.stop(ctx.currentTime + i * 0.15 + 0.3)
    }
    window.setTimeout(() => void ctx.close(), 1000)
  } catch {
    /* no audio output: the panel and its badge still appear */
  }
}

/**
 * The meeting host's lobby (ADR 0014): who is waiting to be let in, polled every 3 s, with Admit,
 * Deny and Admit all. Hidden while nobody waits; a new knock chimes and pulses the badge.
 */
export function LobbyPanel({ credential }: { credential: Credential }) {
  const [knocks, setKnocks] = useState<Knock[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fresh, setFresh] = useState(false)
  const seen = useRef(new Set<string>())
  const credentialRef = useRef(credential)
  credentialRef.current = credential

  const refresh = useCallback(async () => {
    try {
      const next = await listLobby(credentialRef.current)
      if (next.some((k) => !seen.current.has(k.id))) {
        chime()
        setFresh(true)
      }
      for (const k of next) seen.current.add(k.id)
      setKnocks(next)
    } catch {
      /* keep the last list; the next poll retries */
    }
  }, [])

  useEffect(() => {
    void refresh()
    const timer = window.setInterval(refresh, POLL_MS)
    return () => window.clearInterval(timer)
  }, [refresh])

  useEffect(() => {
    if (!fresh) return
    const timer = window.setTimeout(() => setFresh(false), 2000)
    return () => window.clearTimeout(timer)
  }, [fresh])

  const decide = async (ids: string[], admit: boolean) => {
    setBusy(true)
    setError(null)
    try {
      await decideLobby(credentialRef.current, ids, admit)
      setKnocks((list) => list.filter((k) => !ids.includes(k.id)))
    } catch (failure) {
      setError((failure as Error).message || t('moderate.failed'))
    } finally {
      setBusy(false)
    }
  }

  if (knocks.length === 0) return null
  const [before, after] = t('lobby.waiting').split('{count}')
  return (
    <section
      className={`lobby-panel${fresh ? ' lobby-panel--fresh' : ''}`}
      aria-labelledby="lobby-title"
      aria-live="polite"
    >
      <h2 id="lobby-title" className="lobby-panel__title">
        {before}
        <span className="lobby-panel__badge">{knocks.length}</span>
        {after}
      </h2>
      <ul className="lobby-panel__list">
        {knocks.map((k) => (
          <li key={k.id} className="lobby-panel__row">
            <span className="lobby-panel__name" dir="auto">
              {k.name}
            </span>
            {k.guest ? <span className="lobby-panel__tag">{t('lobby.guest')}</span> : null}
            <button
              type="button"
              className="pill-button pill-button--accent"
              disabled={busy}
              aria-label={t('lobby.admitName').replace('{name}', k.name)}
              onClick={() => void decide([k.id], true)}
            >
              {t('lobby.admit')}
            </button>
            <button
              type="button"
              className="pill-button"
              disabled={busy}
              aria-label={t('lobby.denyName').replace('{name}', k.name)}
              onClick={() => void decide([k.id], false)}
            >
              {t('lobby.deny')}
            </button>
          </li>
        ))}
      </ul>
      {knocks.length > 1 ? (
        <button
          type="button"
          className="pill-button pill-button--accent lobby-panel__all"
          disabled={busy}
          onClick={() =>
            void decide(
              knocks.map((k) => k.id),
              true,
            )
          }
        >
          {t('lobby.admitAll')}
        </button>
      ) : null}
      {error ? (
        <p role="alert" className="lobby-panel__error">
          {error}
        </p>
      ) : null}
    </section>
  )
}
